import { useContext, useEffect } from 'react';

import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import { AnalyticsContext } from '@atlassian/atlassian-studio-observability';
import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import { isLastWorkspaceWriteStatus, type LastWorkspaceWriteStatus } from './types';

const LAST_WORKSPACE_WRITE_ANALYTICS_STORAGE_KEY =
	'studio-foundations.last-workspace-write-analytics';
const LAST_WORKSPACE_WRITE_ACTION_SUBJECT = 'studioLastWorkspace';
const LAST_WORKSPACE_WRITE_ACTION = 'persisted';
const LAST_WORKSPACE_WRITE_SOURCE = 'studioFoundationsRouting';

export type LastWorkspaceWriteTrigger = 'noWorkspaceInUrl';

type SendOperationalEvent = (event: {
	action: string;
	actionSubject: string;
	attributes?: AnalyticsAttributes;
	source: string;
}) => Promise<void>;

type QueuedLastWorkspaceWrite = {
	status: LastWorkspaceWriteStatus;
	trigger: LastWorkspaceWriteTrigger;
};

const isQueuedLastWorkspaceWrite = (value: unknown): value is QueuedLastWorkspaceWrite => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}
	const { status, trigger } = value as Record<string, unknown>;
	return isLastWorkspaceWriteStatus(status) && trigger === 'noWorkspaceInUrl';
};

// URLs without a workspace full-page redirect before AnalyticsProvider mounts, so the outcome is
// queued in session storage and sent after the redirect.
export const trackLastWorkspaceWrite = (
	status: Promise<LastWorkspaceWriteStatus>,
	trigger: LastWorkspaceWriteTrigger,
): void => {
	status
		.then((resolvedStatus) => {
			const queuedWrite: QueuedLastWorkspaceWrite = { status: resolvedStatus, trigger };
			return AtlBrowserStorageSession.setItem(
				LAST_WORKSPACE_WRITE_ANALYTICS_STORAGE_KEY,
				JSON.stringify(queuedWrite),
			);
		})
		.catch(() => undefined);
};

const dequeueLastWorkspaceWrite = (): QueuedLastWorkspaceWrite | undefined => {
	try {
		const queuedWrite = AtlBrowserStorageSession.getItem(
			LAST_WORKSPACE_WRITE_ANALYTICS_STORAGE_KEY,
		);
		if (!queuedWrite) {
			return undefined;
		}
		AtlBrowserStorageSession.removeItem(LAST_WORKSPACE_WRITE_ANALYTICS_STORAGE_KEY);
		const parsedWrite: unknown = JSON.parse(queuedWrite);
		return isQueuedLastWorkspaceWrite(parsedWrite) ? parsedWrite : undefined;
	} catch {
		return undefined;
	}
};

export const sendTrackedLastWorkspaceWriteAnalytics = (
	sendOperationalEvent: SendOperationalEvent,
): void => {
	const queuedWrite = dequeueLastWorkspaceWrite();
	if (!queuedWrite) {
		return;
	}

	try {
		sendOperationalEvent({
			action: LAST_WORKSPACE_WRITE_ACTION,
			actionSubject: LAST_WORKSPACE_WRITE_ACTION_SUBJECT,
			source: LAST_WORKSPACE_WRITE_SOURCE,
			attributes: { status: queuedWrite.status, trigger: queuedWrite.trigger },
		}).catch(() => undefined);
	} catch {}
};

export const LastWorkspaceWriteAnalytics = (): null => {
	const analyticsContext = useContext(AnalyticsContext);

	useEffect(() => {
		if (!analyticsContext) {
			return;
		}
		sendTrackedLastWorkspaceWriteAnalytics(analyticsContext.analyticsClient.sendOperationalEvent);
	}, [analyticsContext]);

	return null;
};
